import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const Payment = ({
  amount,
  customerId,
  customerEmail,
  customerPhone,
  firstName,
  AccidentVehicleCode,
  vendorcode,
  vendorType,
  description,
  vehicleNo,
  onSuccess,
  paymentRoute,
  inspectionCode,
}) => {
  // console.log("Payment Component Mounted with props:", {paymentRoute, amount, customerId, AccidentVehicleCode, vendorcode, vendorType, vehicleNo, inspectionCode});

  const hasStartedRef = useRef(false);
  const navigate = useNavigate();
  const isApp = !!window.ReactNativeWebView;

  const isInspection = paymentRoute === "/api/inspection-report-payment";
  const paymentFlow = isInspection ? "inspection" : "accident";

  // ===============================
  // 🔹 Load Razorpay SDK
  // ===============================
  const loadRazorpay = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve(true);

      if (document.getElementById("razorpay-sdk")) {
        return resolve(true);
      }

      const script = document.createElement("script");
      script.id = "razorpay-sdk";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Razorpay SDK failed to load"));

      document.body.appendChild(script);
    });
  };

  const navigateToFailure = () => {
    const queryParams = new URLSearchParams({
      flow: paymentFlow,
      ...(isInspection && inspectionCode
        ? { inspectionCode: String(inspectionCode) }
        : {}),
    });

    navigate(`/payment-failure?${queryParams.toString()}`, {
      replace: true,
      state: {
        flow: paymentFlow,
        inspectionCode: isInspection ? inspectionCode : null,
      },
    });
  };

  /* ===============================
     Main Payment Flow
  =============================== */

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const startPayment = async () => {
      try {
        if (!amount || !customerId) {
          throw new Error("Missing required payment data");
        }

        // ===============================
        // 1️⃣ CREATE ORDER
        // ===============================
        // console.log(`Starting ${paymentFlow} payment flow...`, {amount, customerId, AccidentVehicleCode, vendorcode, vendorType, vehicleNo, inspectionCode});

        let url;
        let bodyData = {
          amount,
          customer_id: customerId,
          customer_email: customerEmail,
          customer_phone: customerPhone,
          first_name: firstName,
          description,
        };

        if (isInspection) {
          url = `${process.env.REACT_APP_BACKEND_URL}${paymentRoute}`;
          bodyData.inspectionCode = inspectionCode;
        } else {
          url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${AccidentVehicleCode}/${vendorcode}/${vendorType}/${vehicleNo}`;
        }
        // console.log(isInspection ? "Creating inspection order..." : "Creating accident order...", {url, bodyData});

        const orderRes = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bodyData),
        });
        let orderJson;

        try {
          orderJson = await orderRes.json();
        } catch {
          throw new Error("Invalid server response");
        }

        if (!orderRes.ok || !orderJson?.data?.id) {
          throw new Error(orderJson?.message || "Order creation failed");
        }

        // ===============================
        // 2️⃣ LOAD SDK
        // ===============================
        await loadRazorpay();

        // ===============================
        // 3️⃣ RAZORPAY OPTIONS
        // ===============================
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY, // Enter the Key ID generated from the Dashboard
          amount: orderJson.data.amount,
          currency: orderJson.data.currency,
          order_id: orderJson.data.id,

          prefill: {
            name: firstName,
            email: customerEmail,
            contact: customerPhone,
          },

          handler: async (response) => {
            try {
              // =========================
              // 🟢 INSPECTION FLOW
              // =========================
              if (isInspection) {
                if (isApp) {
                  window.ReactNativeWebView?.postMessage(
                    JSON.stringify({
                      type: "inspectionPaymentSuccess",
                      inspectionCode,
                      customerId,
                      data: response,
                    }),
                  );
                  return;
                }

                const verifyRes = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/verify-inspection-payment`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(response),
                  },
                );
                const verifyJson = await verifyRes.json();

                if (!verifyRes.ok || !verifyJson?.status) {
                  throw new Error("Inspection verification failed");
                }

                if (typeof onSuccess === "function") {
                  onSuccess(response);
                } else {
                  navigate("/payment-success", { replace: true });
                }

                return;
              }

              // =========================
              // 🔵 ACCIDENT FLOW
              // =========================
              if (isApp) {
                window.ReactNativeWebView?.postMessage(
                  JSON.stringify({
                    type: "paymentSuccess",
                    accidentVehicleCode: AccidentVehicleCode,
                    customerId,
                    vendorType,
                    data: response,
                  }),
                );
                return;
              }

              const verifyRes = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/verify/${AccidentVehicleCode}/${customerId}/${vendorType}`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(response),
                },
              );
              const verifyJson = await verifyRes.json();

              if (!verifyRes.ok || !verifyJson?.status) {
                throw new Error("Verification failed");
              }
              navigate("/payment-success", { replace: true });
            } catch (err) {
              console.error("Verification error:", err);

              if (isApp) {
                window.ReactNativeWebView?.postMessage(
                  JSON.stringify({
                    type: isInspection
                      ? "inspectionPaymentFailure"
                      : "paymentFailure",
                    inspectionCode: isInspection ? inspectionCode : undefined,
                    reason: err.message,
                  }),
                );
              } else {
                navigateToFailure();
              }
            }
          },

          modal: {
            ondismiss: () => {
              console.warn("User closed payment popup");

              if (isApp) {
                window.ReactNativeWebView?.postMessage(
                  JSON.stringify({
                    type: isInspection
                      ? "inspectionPaymentFailure"
                      : "paymentFailure",
                    inspectionCode: isInspection ? inspectionCode : undefined,
                    reason: "USER_CANCELLED",
                  }),
                );
              } else {
                navigateToFailure();
              }
            },
          },
        };

        // ===============================
        // 4️⃣ OPEN CHECKOUT
        // ===============================
        const rzp = new window.Razorpay(options);

        // 🔴 Explicit payment failure
        rzp.on("payment.failed", function (response) {
          console.error("Payment failed:", response.error);

          if (isApp) {
            window.ReactNativeWebView?.postMessage(
              JSON.stringify({
                type: isInspection
                  ? "inspectionPaymentFailure"
                  : "paymentFailure",
                inspectionCode: isInspection ? inspectionCode : undefined,
                reason: response.error.description,
              }),
            );
          } else {
            navigateToFailure();
          }
        });

        rzp.open();
      } catch (err) {
        console.error("Payment initialization error:", err.message);

        if (isApp) {
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: isInspection
                ? "inspectionPaymentFailure"
                : "paymentFailure",
              inspectionCode: isInspection ? inspectionCode : undefined,
              reason: err.message,
            }),
          );
        } else {
          navigateToFailure();
        }
      }
    };

    startPayment();
  }, [
    amount,
    customerId,
    AccidentVehicleCode,
    vendorcode,
    vendorType,
    vehicleNo,
    inspectionCode,
    paymentRoute,
  ]);

  return null;
};

export default Payment;
